import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { useEffect } from "react";

import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";
import Modal from "~/components/Modal";
import type {
	ExportStatus,
} from "~/modules/reports/components/GlobalReportsView/components/UtilisationReportSection/hooks/useExtendedUtilisationExcelReport/useExtendedUtilisationExcelReport.ts";


const AUTO_DISMISS_DELAY_MS = 500;

type StepState = "pending" | "active" | "completed" | "failed";

function StepIcon({ state }: { state: StepState }) {
	switch (state) {
		case "active":
			return <div className="-ml-0.5 size-5 overflow-hidden"><LoadingSpinner theme="neutral"
										scale={0.7} /></div>;
		case "completed":
			return <CheckCircleIcon className="size-5 text-green-500" />;
		case "failed":
			return <XCircleIcon className="size-5 text-red-500" />;
		case "pending":
			return <CheckCircleIcon className="size-5 text-gray-300" />;
	}
}

function getStepStates(status: ExportStatus): [StepState, StepState] {
	switch (status) {
		case "fetching":
			return ["active", "pending"];
		case "generating":
			return ["completed", "active"];
		case "done":
			return ["completed", "completed"];
		case "error":
			return ["failed", "pending"];
		case "idle":
			return ["pending", "pending"];
	}
}

type ExtendedUtilisationReportExportModalProps = {
	status: ExportStatus;
	onCancel: () => void;
	onDismiss: () => void;
};

const ExtendedUtilisationReportExportModal: React.FC<ExtendedUtilisationReportExportModalProps> = ({
	status,
	onCancel,
	onDismiss,
}) => {
	const [fetchState, generateState] = getStepStates(status);
	const isInProgress = status === "fetching" || status === "generating";

	useEffect(() => {
		if (status !== "done") return;
		const timeout = setTimeout(onDismiss, AUTO_DISMISS_DELAY_MS);
		return () => clearTimeout(timeout);
	}, [status, onDismiss]);

	return (
		<Modal
			open={status !== "idle"}
			setOpen={isInProgress ? onCancel : onDismiss}
			closeOnOutsideClick={!isInProgress}
		>
			<div className="space-y-4">
				<h3 className="text-lg font-semibold text-gray-900">
					{status === "error" ? "Export fehlgeschlagen" : "Export wird erstellt"}
				</h3>
				<div className="space-y-3">
					<div className="flex items-center gap-3">
						<StepIcon state={fetchState} />
						<div>
							<span className={`text-sm ${fetchState === "active" ? "font-medium text-gray-900" : fetchState === "failed" ? "font-medium text-red-700" : fetchState === "completed" ? "text-gray-500" : "text-gray-400"}`}>
								{status === "error" ? "Download fehlgeschlagen" : "Auslastungsdaten laden"}
							</span>

						</div>
					</div>
					<div className="flex items-center gap-3">
						<StepIcon state={generateState} />
						<span className={`text-sm ${generateState === "active" ? "font-medium text-gray-900" : generateState === "completed" ? "text-gray-500" : "text-gray-400"}`}>
							Excel Dokument erstellen
						</span>
					</div>
				</div>
				<div className="flex justify-center">
					{status !== "error" && (
						<button
							type="button"
							disabled={status === "done"}
							className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400 disabled:ring-gray-200"
							onClick={onCancel}
						>
							Abbrechen
						</button>
					)}
					{status === "error" && (
						<button
							type="button"
							className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
							onClick={onDismiss}
						>
							Schließen
						</button>
					)}
				</div>
			</div>
		</Modal>
	);
};

export default ExtendedUtilisationReportExportModal;